import { useState } from 'react'
import { Search, X, ChevronDown, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { RedemptionRiskTier } from '@/lib/api-client'

export type RiskFilter = 'all' | RedemptionRiskTier

interface Props {
  search: string
  onSearchChange: (v: string) => void
  riskTier: RiskFilter
  onRiskTierChange: (v: RiskFilter) => void
  minOutstanding: number
  onMinOutstandingChange: (v: number) => void
}

const RISK_OPTS: { value: RiskFilter; label: string; hint: string }[] = [
  { value: 'all',      label: 'All',      hint: 'no filter' },
  { value: 'critical', label: 'Critical', hint: 'outstanding ≥ 3 · 60-90 days gone' },
  { value: 'warning',  label: 'Warning',  hint: 'outstanding ≥ 1 · 30+ days gone' },
  { value: 'active',   label: 'Active',   hint: 'outstanding ≥ 1 · visited last 30 days' },
  { value: 'churn',    label: 'Churn',    hint: 'outstanding ≥ 3 · 90+ days gone' },
]

const MIN_OUTSTANDING_OPTS = [1, 2, 3, 5, 10]

export function RedemptionToolbar({
  search,
  onSearchChange,
  riskTier,
  onRiskTierChange,
  minOutstanding,
  onMinOutstandingChange,
}: Props) {
  const [riskOpen, setRiskOpen] = useState(false)
  const [minOpen, setMinOpen] = useState(false)
  const currentRiskLabel = RISK_OPTS.find((o) => o.value === riskTier)?.label ?? 'All'

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search name, PNO, or phone..."
          className="pl-9 pr-9"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Popover open={riskOpen} onOpenChange={setRiskOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="justify-between min-w-[140px]">
            Risk: {currentRiskLabel}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-1" align="start">
          {RISK_OPTS.map((o) => (
            <button
              key={o.value}
              onClick={() => {
                onRiskTierChange(o.value)
                setRiskOpen(false)
              }}
              className="w-full flex items-start gap-2 px-3 py-2 text-left text-sm rounded-md hover:bg-hover/50"
            >
              <Check className={`h-4 w-4 mt-0.5 ${riskTier === o.value ? 'opacity-100' : 'opacity-0'}`} />
              <div>
                <div className="font-medium">{o.label}</div>
                <div className="text-xs text-text-muted">{o.hint}</div>
              </div>
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <Popover open={minOpen} onOpenChange={setMinOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="justify-between min-w-[140px]">
            Min outstanding: {minOutstanding}+
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="start">
          {MIN_OUTSTANDING_OPTS.map((n) => (
            <button
              key={n}
              onClick={() => {
                onMinOutstandingChange(n)
                setMinOpen(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md hover:bg-hover/50"
            >
              <Check className={`h-4 w-4 ${minOutstanding === n ? 'opacity-100' : 'opacity-0'}`} />
              <span>{n}+ sessions</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  )
}
